import { useMutation, useQueryClient } from "@tanstack/react-query";
import { chatApi } from "../../api/chat";
import { SendMessageRequest } from "../../types/sendMessage";
import { findUrlsInText, buildAttachmentMetadata, buildLinkPreviewMetadata } from "../../utils/messageUtils";

/**
 * Hook để gửi tin nhắn với hỗ trợ text, đính kèm, link và trả lời
 * @param roomId - ID phòng chat
 * @param currentUserId - ID người dùng hiện tại cho optimistic update
 * @returns Mutation object với hàm sendMessage
 */
export const useSendMessage = (roomId: string, currentUserId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ content, attachments = [], replyMetaData, metadata: customMetadata }: SendMessageRequest) => {
      const hasAttachments = attachments.length > 0;
      let metadata: Record<string, any> | undefined;

      // 1. Xử lý custom metadata (image, album, v.v.)
      if (customMetadata) {
        metadata = customMetadata;
      }

      // 2. Xử lý đính kèm
      if (hasAttachments) {
        metadata = {
          ...metadata,
          ...buildAttachmentMetadata(attachments),
        };
      }

      // 3. Xử lý xem trước link - CHỈ khi tin nhắn là URL thuần túy (giống Messenger, Slack)
      // Logic: Link preview CHỈ hiển thị khi:
      // - Không có attachments, reply, custom metadata
      // - Content là URL thuần túy (không có text khác kèm theo)
      // Nếu có text + URL → Hiển thị text bình thường với URL clickable
      if (!hasAttachments && !replyMetaData && !customMetadata) {
        const urls = findUrlsInText(content);
        const trimmedContent = content.trim();

        // Chỉ preview nếu content CHÍNH là URL (không có text khác)
        // Ví dụ: "https://baomoi.com/" → Preview
        // Ví dụ: "Check https://baomoi.com/" → KHÔNG preview (text bình thường)
        if (urls.length > 0 && trimmedContent === urls[0]) {
          try {
            const linkReview = await chatApi.getLinkPreview(urls[0]);

            if (linkReview.Data) {
              metadata = buildLinkPreviewMetadata(urls[0], linkReview.Data);
            }
          } catch (error) {
            console.warn("Không lấy được link preview:", error);
          }
        }
      }

      // 4. Xử lý metadata trả lời
      if (replyMetaData) {
        metadata = {
          ...metadata,
          ...replyMetaData,
        };
      }

      // 5. Xác định nội dung tin nhắn
      const messageContent = hasAttachments ? attachments[0].url : content;

      // 6. Gửi tin nhắn
      const response = await chatApi.sendMessage(roomId, messageContent, metadata);

      return response;
    },

    onSuccess: () => {
      // KHÔNG invalidate infinite-messages - để realtime message xử lý!
      // Realtime message có thông tin người gửi chính xác từ WebSocket
      // Chỉ invalidate resources để cập nhật danh sách media/files
      queryClient.invalidateQueries({ queryKey: ["resources", roomId] });
    },

    onError: (error) => {
      console.error("Gửi tin nhắn thất bại:", error);
    },
  });
};
